// =============================================================================
// Prospect Data Room List Page (Phase II)
// =============================================================================

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FolderOpenIcon,
  LockClosedIcon,
  DocumentCheckIcon,
  ArrowRightIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { Card, DealImage } from '../../components/ui';
import { fundraisingService } from '../../services/fundraising.service';
import { getApiError } from '../../services/api';
import type { ProspectDataRoom } from '../../types/fundraising';

// =============================================================================
// Component
// =============================================================================

export function DataRoomListPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [dataRooms, setDataRooms] = useState<ProspectDataRoom[]>([]);
  const [error, setError] = useState<string | null>(null);

  const loadDataRooms = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await fundraisingService.getProspectDataRooms();
      setDataRooms(data);
    } catch (err) {
      console.error('Failed to load data rooms:', err);
      setError(getApiError(err).message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDataRooms();
  }, [loadDataRooms]);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    });
  };

  const formatCurrency = (amount: number | null) => {
    if (amount === null) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="h-64 bg-gray-200 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Data Rooms</h1>
        <p className="text-sm text-gray-500 mt-1">
          Access investment opportunity materials and documents
        </p>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Data Rooms Grid */}
      {dataRooms.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <FolderOpenIcon className="h-12 w-12 text-gray-300 mx-auto" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No data rooms available</h3>
            <p className="mt-2 text-sm text-gray-500">
              You do not have access to any data rooms at this time.
              Contact your relationship manager for access.
            </p>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {dataRooms.map((room) => (
            <Card key={room.accessId} padding="none" className="overflow-hidden">
              {/* Deal Image */}
              <div className="h-40 bg-gray-100">
                <DealImage
                  dealId={room.deal.id}
                  dealName={room.deal.name}
                  imageUrl={room.deal.imageUrl}
                  className="w-full h-full object-cover"
                  fallbackClassName="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary-600 to-primary-800"
                  iconClassName="h-16 w-16 text-white/30"
                />
              </div>

              {/* Content */}
              <div className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{room.deal.name}</h3>
                    <p className="text-sm text-gray-500 mt-0.5">{room.deal.location}</p>
                  </div>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 capitalize">
                    {room.deal.propertyType.toLowerCase().replace('_', ' ')}
                  </span>
                </div>

                {/* Deal Info */}
                <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Target Raise</span>
                    <p className="font-medium text-gray-900">{formatCurrency(room.deal.targetRaise)}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Status</span>
                    <p className="font-medium text-gray-900 capitalize">
                      {room.deal.status.toLowerCase()}
                    </p>
                  </div>
                </div>

                {/* Access Status */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  {room.ndaRequired && !room.ndaSignedAt ? (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-amber-600">
                        <LockClosedIcon className="h-5 w-5" />
                        <span className="text-sm font-medium">NDA Required</span>
                      </div>
                      <button
                        onClick={() => navigate(`/prospect/datarooms/${room.deal.id}/nda`)}
                        className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700"
                      >
                        Sign NDA <ArrowRightIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-green-600">
                        <DocumentCheckIcon className="h-5 w-5" />
                        <span className="text-sm font-medium">Access Granted</span>
                      </div>
                      <button
                        onClick={() => navigate(`/prospect/datarooms/${room.deal.id}`)}
                        className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700"
                      >
                        View Documents <ArrowRightIcon className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Last Accessed */}
                {room.lastAccessedAt && (
                  <div className="mt-3 flex items-center gap-1 text-xs text-gray-400">
                    <ClockIcon className="h-3.5 w-3.5" />
                    Last accessed {formatDate(room.lastAccessedAt)}
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export default DataRoomListPage;
